// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, ChangeDetectionStrategy, input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { TimeRange, TimeRangeValue } from '@lfx/key-projects/entities';

export { TimeRangeValue } from '@lfx/key-projects/entities';

@Component({
  selector: 'lfx-time-range-selector',
  imports: [CommonModule, NgbDropdownModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="time-range-selector" ngbDropdown>
      <button
        class="time-range-toggle"
        ngbDropdownToggle
        [disabled]="disabled()"
        [attr.aria-label]="ariaLabel()"
      >
        <i class="fal fa-calendar toggle-icon"></i>
        <span class="toggle-label">{{ selectedLabel() }}</span>
        <i class="fal fa-chevron-down chevron-icon"></i>
      </button>

      <div class="time-range-menu" ngbDropdownMenu>
        @for (option of options; track option.value) {
          <button
            class="time-range-item"
            [class.selected]="isSelected(option.value)"
            (click)="onOptionClick(option.value)"
            ngbDropdownItem
          >
            <span class="item-left">
              <span class="item-check">
                @if (isSelected(option.value)) {
                  <i class="fal fa-check"></i>
                }
              </span>
              <span class="item-label">{{ option.label }}</span>
            </span>
            @if (getDateRange(option.value)) {
              <span class="item-date-range">{{ getDateRange(option.value) }}</span>
            }
          </button>
        }
      </div>
    </div>
  `,
  styleUrls: ['./time-range-selector.component.scss'],
})
export class TimeRangeSelectorComponent {
  readonly selectedRange = input<TimeRangeValue>(TimeRange.DEFAULT);
  readonly disabled = input<boolean>(false);
  readonly rangeSelected = output<TimeRangeValue>();

  protected readonly options = TimeRange.OPTIONS;

  protected readonly selectedLabel = computed(() => TimeRange.getLabel(this.selectedRange()));
  protected readonly ariaLabel = computed(() => `Time range: ${this.selectedLabel()}`);

  protected isSelected(value: TimeRangeValue): boolean {
    return this.selectedRange() === value;
  }

  protected getDateRange(value: TimeRangeValue): string {
    return TimeRange.getDateRangeDisplay(value);
  }

  protected onOptionClick(value: TimeRangeValue): void {
    if (value !== this.selectedRange()) {
      this.rangeSelected.emit(value);
    }
  }
}
